<template>
  <div class="ms-4 mt-3 mb-3">
    <p class="text-xl fw-bold mb-0">Compliance Centre</p>
    <p class="text-xs text-dark mb-0">
      Monitor entity compliance status, manage documents, and ensure regulatory adherence.
    </p>
  </div>
  <div class="compliance-centre px-4 p-2">
    <div v-if="!resolved_entity_profile_id" class="card shadow rounded-lg p-4">
      <p class="fw-bold mb-1">Compliance data unavailable</p>
      <p class="text-muted text-xs mb-0">
        Open this view from an entity profile so the component can load its compliance records.
      </p>
    </div>

    <div v-else-if="is_loading" class="card shadow rounded-lg p-4">
      <p class="fw-bold mb-1">Loading compliance overview...</p>
      <p class="text-muted text-xs mb-0">
        Pulling live rule status and review dates from compliance management.
      </p>
    </div>

    <div v-else class="row">

      <div class="col-12 col-md-6 mb-3">
        <div class="card shadow p-3 rounded-lg h-100">
          <p class="text-x fw-bold mb-2">Compliance Status Heat Map</p>
          <p class="text-muted text-xs mb-2">Quarter-end compliance status for the current year and previous 3 years</p>

          <div class="d-flex gap-3 mb-3 mt-3 align-items-center flex-wrap">
            <span class="legend-item text-dark text-xxs">
              <span class="legend-dot bg-fully"></span> Compliant
            </span>
            <span class="legend-item text-dark text-xxs">
              <span class="legend-dot bg-partial"></span> Pending
            </span>
            <span class="legend-item text-dark text-xxs">
              <span class="legend-dot bg-non"></span> Overdue
            </span>
            <span class="legend-item text-dark text-xxs">
              <span class="legend-dot bg-pending"></span> Not Started
            </span>
          </div>

          <div v-if="heat_map.length" class="table-responsive">
            <table class="table table-lined align-middle text-sm">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Q1</th>
                  <th>Q2</th>
                  <th>Q3</th>
                  <th>Q4</th>
                  <th class="text-end">Score</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in heat_map" :key="row.year">
                  <td class="fw-bold">{{ row.year }}</td>
                  <td><span class="heat" :class="heat_class(row.q1)"></span></td>
                  <td><span class="heat" :class="heat_class(row.q2)"></span></td>
                  <td><span class="heat" :class="heat_class(row.q3)"></span></td>
                  <td><span class="heat" :class="heat_class(row.q4)"></span></td>
                  <td class="text-end fw-bold">{{ row.score }}%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="empty-state">
            <p class="fw-bold mb-1">No heat map history found</p>
            <p class="text-muted text-xs mb-0">
              Compliance rules have not produced enough historical data for quarter-end scoring yet.
            </p>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6 mb-3">
        <div class="card shadow p-3 rounded-lg h-100">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <p class="text-x fw-bold mb-0">Rule Review Status</p>
              <p class="text-muted text-xs mb-0">Latest countdown status for the most important rules</p>
            </div>
            <div class="text-end">
              <div class="fs-5 fw-bold text-warning">{{ integrity_score }}%</div>
              <small class="text-muted">Rule Health</small>
            </div>
          </div>

          <div class="alert" :class="attention_alert_class">
            <strong>{{ attention_summary_title }}</strong><br />
            <span class="text-xs">{{ attention_summary_text }}</span>
          </div>

          <div
            v-for="item in integrity_checks"
            :key="item.id"
            class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center border rounded-lg p-3 mb-2"
            :class="status_class(item.status)"
          >
            <div>
              <p class="fw-bold text-xs mb-1">{{ item.title }}</p>
              <p class="text-muted small mb-0 text-xs">{{ item.description }}</p>
            </div>
            <div class="text-end small text-xs mt-2 mt-md-0">
              <span class="fw-bold">{{ status_label(item.status) }}</span><br />
              <span class="text-muted text-xs">{{ item.meta_label }}: {{ item.checked }}</span>
            </div>
          </div>

          <div v-if="!integrity_checks.length" class="empty-state">
            <p class="fw-bold mb-1">No rule statuses available</p>
            <p class="text-muted text-xs mb-0">
              This profile does not have any compliance rule countdown data yet.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  GET_ENTITY_COMPLIANCE_COUNTDOWN,
  GET_ENTITY_COMPLIANCE_HEAT_MAP_URL,
  GET_ENTITY_COMPLIANCE_OVERVIEW_URL,
  get_headers,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from "axios";

export default {
  name: "ComplianceCentre",
  mixins: [alert_mixin],

  props: {
    entity_profile_id: {
      type: [String, Number],
      default: null,
    },
  },

  data: () => ({
    headers: get_headers(),
    is_loading: false,
    overview_data: null,
    countdown_data: [],
    heat_map_data: [],
    pending_requests: 0,
  }),

  computed: {
    resolved_entity_profile_id() {
      const route_id = this.$route?.query?.entity_profile_id;
      const stored_id = sessionStorage.getItem("entity_profile_id");
      return this.entity_profile_id || route_id || stored_id || null;
    },

    actionable_items() {
      return Array.isArray(this.overview_data?.actionable_items)
        ? this.overview_data.actionable_items
        : [];
    },

    actionable_items_count() {
      return this.actionable_items.length;
    },

    overdue_items_count() {
      return this.actionable_items.filter((item) => item.priority === "red").length;
    },

    pending_items_count() {
      return this.actionable_items.filter((item) => item.priority !== "red").length;
    },

    total_rules_count() {
      return this.countdown_data.length || Number(this.overview_data?.total_rules || 0);
    },

    compliant_rules_count() {
      return this.countdown_data.filter((item) => this.normalize_rule_status(item.status) === "compliant").length;
    },

    integrity_score() {
      if (!this.total_rules_count) return 0;
      return Math.round((this.compliant_rules_count / this.total_rules_count) * 100);
    },

    heat_map() {
      const heat_map_data = Reflect.get(this, "heat_map_data") || [];
      const result = Array.isArray(heat_map_data) ? heat_map_data : [];
      return result;
    },

    integrity_checks() {
      return [...this.countdown_data]
        .sort((first, second) => this.compare_rule_priority(first, second))
        .slice(0, 5)
        .map((item) => ({
          id: item.rule_id,
          title: item.rule_name || "Compliance Rule",
          description: item.description || item.outcome || "Compliance rule review",
          status: this.normalize_rule_status(item.status),
          checked: this.format_date(item.next_eval_date),
          meta_label: "Next review",
        }));
    },

    attention_alert_class() {
      if (this.overdue_items_count > 0) return "alert-danger";
      if (this.pending_items_count > 0) return "alert-warning";
      return "alert-success";
    },

    attention_summary_title() {
      if (this.overdue_items_count > 0) return "Immediate attention required";
      if (this.pending_items_count > 0) return "Reviews are still pending";
      return "Everything is currently on track";
    },

    attention_summary_text() {
      if (this.actionable_items_count === 0) {
        return "No actionable compliance items were returned for this profile.";
      }

      return `${this.actionable_items_count} actionable item(s) were identified in the latest evaluation cycle.`;
    },
  },

  watch: {
    resolved_entity_profile_id: {
      immediate: true,
      handler(new_value) {
        if (new_value) {
          this.load_compliance_data();
          return;
        }

        this.overview_data = null;
        this.countdown_data = [];
      },
    },
  },

  methods: {
    load_compliance_data() {
      if (!this.resolved_entity_profile_id) return;

      this.begin_request();
      this.load_overview_data();
      this.load_countdown_data();
      this.load_heat_map_data();
    },

    load_overview_data() {
      const request_config = this.build_request_config();

      axios
        .get(GET_ENTITY_COMPLIANCE_OVERVIEW_URL, request_config)
        .then((response) => {
          const response_body = Reflect.get(response, "data") || {};
          const response_status = Reflect.get(response_body, "status");
          const response_data = Reflect.get(response_body, "data");
          this.overview_data = response_status === "success" ? response_data : null;
        })
        .catch((error) => {
          this.overview_data = null;
          this.handle_load_error(error, "overview");
        })
        .finally(() => {
          this.finish_request();
        });
    },

    load_countdown_data() {
      const request_config = this.build_request_config();

      axios
        .get(GET_ENTITY_COMPLIANCE_COUNTDOWN, request_config)
        .then((response) => {
          const response_body = Reflect.get(response, "data") || {};
          const response_status = Reflect.get(response_body, "status");
          const response_data = Reflect.get(response_body, "compliance_countdown");
          const countdown_data = response_status === "success" ? response_data || [] : [];
          this.countdown_data = countdown_data;
        })
        .catch((error) => {
          this.countdown_data = [];
          this.handle_load_error(error, "countdown");
        })
        .finally(() => {
          this.finish_request();
        });
    },

    load_heat_map_data() {
      const request_config = this.build_request_config();

      axios
        .get(GET_ENTITY_COMPLIANCE_HEAT_MAP_URL, request_config)
        .then((response) => {
          const response_body = Reflect.get(response, "data") || {};
          const response_status = Reflect.get(response_body, "status");
          const response_data = Reflect.get(response_body, "data");
          const heat_map_data = response_status === "success" ? response_data || [] : [];
          this.heat_map_data = heat_map_data;
        })
        .catch((error) => {
          this.heat_map_data = [];
          this.handle_load_error(error, "heat map");
        })
        .finally(() => {
          this.finish_request();
        });
    },

    build_request_config() {
      const request_config = {
        headers: this.headers,
        params: {
          entity_profile_id: this.resolved_entity_profile_id,
        },
      };
      return request_config;
    },

    begin_request() {
      this.pending_requests = 3;
      this.is_loading = true;
    },

    finish_request() {
      const pending_requests = Math.max((Reflect.get(this, "pending_requests") || 0) - 1, 0);
      this.pending_requests = pending_requests;
      this.is_loading = pending_requests > 0;
    },

    handle_load_error(error, label) {
      const response_data = Reflect.get(Reflect.get(error, "response") || {}, "data") || {};
      const error_message = Reflect.get(response_data, "message") || Reflect.get(error, "message") || "Unknown error";
      const message = `Failed to load compliance ${label}: ${error_message}`;
      this.show_error(message);
    },

    heat_class(val) {
      return {
        green: "heat-green",
        yellow: "heat-yellow",
        red: "heat-red",
        gray: "heat-gray",
      }[val];
    },

    status_class(status) {
      if (status === "compliant") return "bg-success-subtle border-success";
      if (status === "overdue") return "bg-danger-subtle border-danger";
      if (status === "pending") return "bg-warning-subtle border-warning";
      return "bg-light border-secondary";
    },

    status_label(status) {
      if (status === "compliant") return "Compliant";
      if (status === "overdue") return "Overdue";
      if (status === "pending") return "Pending";
      return "Not Started";
    },

    normalize_rule_status(status) {
      const normalized_status = String(status || "").toLowerCase();

      if (normalized_status.includes("overdue")) return "overdue";
      if (normalized_status.includes("pending")) return "pending";
      if (normalized_status.includes("compliant")) return "compliant";
      if (normalized_status.includes("not started")) return "not_started";
      return "not_started";
    },

    compare_rule_priority(first, second) {
      const first_status = this.normalize_rule_status(first.status);
      const second_status = this.normalize_rule_status(second.status);
      const rank = {
        overdue: 0,
        pending: 1,
        not_started: 2,
        compliant: 3,
      };

      if (rank[first_status] !== rank[second_status]) {
        return rank[first_status] - rank[second_status];
      }

      const first_date = first.next_eval_date ? new Date(first.next_eval_date).getTime() : Number.MAX_SAFE_INTEGER;
      const second_date = second.next_eval_date ? new Date(second.next_eval_date).getTime() : Number.MAX_SAFE_INTEGER;
      return first_date - second_date;
    },

    format_date(value) {
      if (!value) return "N/A";

      const parsed_date = new Date(value);
      if (Number.isNaN(parsed_date.getTime())) return "N/A";

      return parsed_date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

/* Heat Map colors */
.bg-fully { background-color: #38b000; }
.bg-partial { background-color: #f5a623; }
.bg-non { background-color: #ff3b30; }
.bg-pending { background-color: #e5e5e5; }

.heat {
  display: inline-block;
  width: 48px;
  height: 20px;
  border-radius: 6px;
}

.heat-green { background: #3ccf91; }
.heat-yellow { background: #f7b733; }
.heat-red { background: #e74c3c; }
.heat-gray { background: #ecf0f1; }

.empty-state {
  padding: 1rem 0;
}

@media (max-width: 1024px) {
  .compliance-centre {
    padding: 1rem !important;
  }

  .heat {
    width: 36px;
    height: 18px;
  }

  .legend-item {
    font-size: 0.75rem;
  }
}

@media (max-width: 768px) {
  .heat {
    width: 30px;
    height: 16px;
  }

  .text-x {
    font-size: 0.95rem;
  }
}
</style>
